import React, { useState, useEffect } from 'react';
import { Search, MessageSquare, Clock, Package, AlertCircle, Mail, Phone, User, Calendar, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Type Definition for Chat Leads
interface Lead {
    conversationId: number;
    buyerId: number;
    buyerName: string;
    buyerEmail: string;
    productName: string;
    productId: number;
    lastMessage: string;
    lastMessageTime: string;
    unreadCount: number;
}

// Type Definition for Inquiries
interface Inquiry {
    id: number;
    buyer_id: number | null; // Nullable agar guest hai
    name: string;
    email: string;
    mobile: string;
    message: string;
    quantity: number;
    created_at: string;
    product_id: number;
    productName: string; // Backend join se aa raha hai
    productImage: string; // Backend join se aa raha hai
}

const LeadsPage: React.FC = () => {
    const navigate = useNavigate();
    
    // --- STATE ---
    const [activeTab, setActiveTab] = useState<'chats' | 'inquiries'>('chats'); 
    const [leads, setLeads] = useState<Lead[]>([]); 
    const [inquiries, setInquiries] = useState<Inquiry[]>([]); 
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const userStr = localStorage.getItem('currentUser'); 
    const seller = userStr ? JSON.parse(userStr) : null;

    // --- FETCH DATA ---
    useEffect(() => {
        const fetchData = async () => {
            if (!seller || !seller.id) {
                setLoading(false);
                return;
            }

            try {
                // 1. Fetch Chat Leads
                const chatRes = await fetch(`/nodejs/api/chat/leads/${seller.id}`);
                const chatData = await chatRes.json();
                if (Array.isArray(chatData)) setLeads(chatData);

                // 2. Fetch Inquiries
                const inqRes = await fetch(`/nodejs/api/inquiry/seller/${seller.id}`);
                if (inqRes.ok) {
                    const inqData = await inqRes.json();
                    if (Array.isArray(inqData)) setInquiries(inqData);
                }

            } catch (err) {
                console.error("Error fetching data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);

    }, [seller?.id]);

    // --- FILTERING ---
    const filteredLeads = leads.filter(lead => 
        (lead.buyerName && lead.buyerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.productName && lead.productName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const filteredInquiries = inquiries.filter(inq => 
        (inq.name && inq.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (inq.email && inq.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // --- ACTIONS ---
    const openChat = (lead: Lead) => {
        navigate(`/chat/${lead.conversationId}?buyerId=${lead.buyerId}&buyerName=${encodeURIComponent(lead.buyerName)}&productName=${encodeURIComponent(lead.productName)}`);
    };

    // --- NEW: Handle Inquiry Chat ---
    const handleInquiryChat = async (inq: Inquiry) => {
        // 1. Check if Guest User
        if (!inq.buyer_id) {
            alert("This inquiry is from a Guest User (Not Registered). You can only contact them via Email or Phone.");
            return;
        }

        try {
            // 2. Get or Create Conversation
            const res = await fetch(`/nodejs/api/chat/conversation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    buyerId: inq.buyer_id,
                    sellerId: seller.id,
                    productId: inq.product_id
                })
            });

            const data = await res.json();
            
            if (data.conversationId) {
                // 3. Navigate to Chat
                navigate(`/chat/${data.conversationId}?buyerId=${inq.buyer_id}&buyerName=${encodeURIComponent(inq.name)}&productName=${encodeURIComponent(inq.productName)}&productImage=${encodeURIComponent(inq.productImage || '')}`);
            }
        } catch (error) {
            console.error("Chat Error:", error);
            alert("Could not start chat. Server error.");
        }
    };

    const formatTime = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (!seller) return <div>Access Denied</div>;
    if (loading) return <div className="p-10 text-center text-blue-600 font-bold animate-pulse">Loading...</div>;

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <h1 className="text-2xl font-extrabold text-gray-900 mb-6 flex items-center gap-2">
                <MessageSquare className="text-blue-600" /> Leads & Inquiries
            </h1>

            {/* --- TOP CONTROLS --- */}
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-6">
                <div className="bg-white p-1 rounded-xl border border-gray-200 shadow-sm flex">
                    <button onClick={() => setActiveTab('chats')} className={`px-6 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'chats' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}>
                        <MessageSquare size={16} /> Live Chats <span className="bg-white/20 px-2 py-0.5 rounded text-xs">{leads.length}</span>
                    </button>
                    <button onClick={() => setActiveTab('inquiries')} className={`px-6 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'inquiries' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}>
                        <Mail size={16} /> Product Inquiries <span className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded text-xs ml-1">{inquiries.length}</span>
                    </button>
                </div>

                <div className="bg-white p-1 rounded-xl border border-gray-200 shadow-sm w-full md:w-auto">
                    <div className="flex items-center w-full md:w-64 rounded-lg bg-gray-50 px-3 py-2">
                        <Search size={18} className="text-gray-400" />
                        <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-transparent focus:outline-none text-sm text-gray-700 ml-2" />
                    </div>
                </div>
            </div>

            {/* --- VIEW 1: CHATS --- */}
            {activeTab === 'chats' && (
                <div className="grid grid-cols-1 gap-4 animate-fade-in">
                    {filteredLeads.map((lead) => (
                        <div key={lead.conversationId} onClick={() => openChat(lead)} className="group bg-white p-5 rounded-xl border border-gray-200 hover:shadow-lg cursor-pointer flex items-center justify-between relative transition-all">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                                    {lead.buyerName ? lead.buyerName.charAt(0).toUpperCase() : 'B'}
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">{lead.buyerName} {lead.unreadCount > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">NEW</span>}</h3>
                                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-1"><Package size={12} className="text-blue-500" /> <span className="font-medium text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">{lead.productName}</span></div>
                                    <p className="text-sm text-gray-500 mt-1.5 truncate max-w-xs italic">{lead.lastMessage || 'Start a conversation...'}</p>
                                </div>
                            </div>
                            <div className="text-right flex flex-col items-end gap-2">
                                <span className="text-xs font-semibold text-gray-400"><Clock size={10} className="inline mr-1" />{lead.lastMessageTime ? new Date(lead.lastMessageTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'New'}</span>
                                <button className="text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors">Reply &rarr;</button>
                            </div>
                        </div>
                    ))}
                    {filteredLeads.length === 0 && <div className="text-center py-10 text-gray-500">No live chats found.</div>}
                </div>
            )}

            {/* --- VIEW 2: INQUIRIES (UPDATED WITH CHAT BUTTON) --- */}
            {activeTab === 'inquiries' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
                    {filteredInquiries.length > 0 ? (
                        filteredInquiries.map((inq) => (
                            <div key={inq.id} className="bg-white p-5 rounded-xl border border-gray-200 hover:shadow-lg transition-all relative">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold">
                                            <Mail size={18} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-800">{inq.name}</h3>
                                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                                <Calendar size={10} /> {formatTime(inq.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <span className="bg-blue-50 text-blue-700 text-xs font-bold px-2 py-1 rounded border border-blue-100">Qty: {inq.quantity}</span>
                                        <span className="text-[10px] text-gray-400 truncate max-w-[100px]">{inq.productName}</span>
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 mb-4">
                                    <p className="text-sm text-gray-600 italic">"{inq.message}"</p>
                                </div>

                                <div className="space-y-2 mb-4">
                                    <div className="flex items-center gap-2 text-sm text-gray-700"><Mail size={14} className="text-gray-400" /> {inq.email}</div>
                                    <div className="flex items-center gap-2 text-sm text-gray-700"><Phone size={14} className="text-gray-400" /> {inq.mobile}</div>
                                </div>

                                {/* ACTION BUTTONS */}
                                <div className="flex gap-2 mt-auto">
                                    {/* Call Button */}
                                    <a href={`tel:${inq.mobile}`} className="flex-1 flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-bold hover:bg-gray-50 transition">
                                        <Phone size={16} /> Call
                                    </a>

                                    {/* NEW: Chat Button */}
                                    {inq.buyer_id ? (
                                        <button 
                                            onClick={() => handleInquiryChat(inq)}
                                            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2 rounded-lg text-sm font-bold hover:bg-blue-700 transition"
                                        >
                                            <MessageCircle size={16} /> Chat
                                        </button>
                                    ) : (
                                        // Guest users ke liye Email button
                                        <a href={`mailto:${inq.email}`} className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2 rounded-lg text-sm font-bold hover:bg-blue-700 transition">
                                            <Mail size={16} /> Email
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-full flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
                            <div className="bg-gray-100 p-4 rounded-full mb-3"><Mail size={32} className="text-gray-400" /></div>
                            <p className="text-gray-500 font-medium">No inquiries received yet.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LeadsPage;